import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// React's own `lazy` constrains its type parameter to `ComponentType<any>` (not
// `<unknown>`): component props are contravariant, so a page declaring concrete
// (even optional) props is NOT assignable to `ComponentType<unknown>` and would
// make `T` infer as `never`. We mirror React's constraint exactly so any route
// component — including those with props — is accepted.
// oxlint-disable-next-line typescript/no-explicit-any -- must match React.lazy's own ComponentType<any> constraint (see comment above)
type AnyComponent = ComponentType<any>;

/**
 * Factory shape produced by a dynamic `import()` of a route component — matches
 * both patterns used in src/App.tsx:
 *   - default export:  () => import('@/pages/X')
 *   - named export:    () => import('@/pages/X').then((m) => ({ default: m.X }))
 */
type ImportFactory<T extends AnyComponent> = () => Promise<{ default: T }>;

interface RetryOptions {
  /** Number of RETRIES after the initial attempt (default 2 → 3 attempts total). */
  retries?: number;
  /** Base backoff in ms; doubles each retry (default 200ms → 200, 400, ...). */
  backoffMs?: number;
}

const DEFAULT_RETRIES = 2;
const DEFAULT_BACKOFF_MS = 200;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Invoke a dynamic-import factory, retrying the SAME module URL a few times with
 * exponential backoff before giving up.
 *
 * Retrying the same URL (no cache-busting query param, no side effects) resolves
 * transient edge/network blips during a deploy window silently. A genuinely
 * missing chunk exhausts the retries and re-throws, so the error surfaces to
 * Suspense → RouteErrorBoundary exactly as it would today (worst case == current
 * behavior; best case == the user never sees a reload). See GW-8398.
 */
export async function retryImport<T extends AnyComponent>(
  factory: ImportFactory<T>,
  opts: RetryOptions = {}
): Promise<{ default: T }> {
  const retries = opts.retries ?? DEFAULT_RETRIES;
  const backoffMs = opts.backoffMs ?? DEFAULT_BACKOFF_MS;

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;
      // No delay after the final attempt — we're about to re-throw.
      if (attempt < retries) {
        await delay(backoffMs * 2 ** attempt);
      }
    }
  }
  throw lastError;
}

/**
 * Drop-in replacement for `React.lazy` that retries a failed dynamic import
 * before surfacing the error to the nearest error boundary.
 */
export function lazyWithRetry<T extends AnyComponent>(
  factory: ImportFactory<T>,
  opts?: RetryOptions
): LazyExoticComponent<T> {
  return lazy(() => retryImport(factory, opts));
}
